import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import {
  getSelectedOrder,
  updateDeliveryDate,
  resetSelectedOrder,
  orderStatusChange,
  resetOrderStatusUpdate,
} from '../../actions/adminOrdersDash';
import { useHistory, useLocation, useParams } from 'react-router-dom';

const ucFirst = (str: string) =>
  str.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const customerType = (orderCount: number) => {
  if (orderCount <= 1) return { label: 'New', color: '#6d7175', bg: '#f1f1f1' };
  if (orderCount > 5) return { label: 'Loyal', color: '#065f46', bg: '#d1fae5' };
  return { label: 'Returning', color: '#004085', bg: '#cce5ff' };
};

const infoRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '8px 0',
  borderBottom: '1px solid #f1f1f1',
  fontSize: '0.85rem',
  color: '#202223',
};

const sectionLabelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: '#6d7175',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const SelectedOrder = (props: any) => {
  const history = useHistory();
  const location = useLocation();
  const params: any = useParams();

  const [order, setOrder] = useState<any>({});
  const [deliveryDate, setDeliveryDate] = useState<any>('');
  const [showConfirmDelivery, setShowConfirmDelivery] = useState(false);
  const [showUpdateMessage, setShowUpdateMessage] = useState(false);
  const [updateClicked, setUpdateClicked] = useState(false);
  const [adminOrderMessage, setAdminOrderMessage] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [showWarning, setShowWarning] = useState(false);
  const [orderStatusUpdated, setOrderStatusUpdated] = useState(false);

  const prevLocationPathnameRef = useRef(location.pathname);
  const prevAdminOrdersRef = useRef<any>(props.adminOrders);

  useEffect(() => {
    const locationState: any = location.state;
    if (!locationState) {
      props.getSelectedOrder(params.orderid);
    } else if (locationState.order) {
      setOrder(locationState.order);
      setOrderStatus(locationState.order.orderStatus);
    }
    return () => { props.resetSelectedOrder(); };
  }, []);

  useEffect(() => {
    const prevAdminOrders = prevAdminOrdersRef.current;
    prevAdminOrdersRef.current = props.adminOrders;

    if (props.adminOrders.orderStatusUpdated !== prevAdminOrders.orderStatusUpdated && props.adminOrders.orderStatusUpdated) {
      setOrderStatusUpdated(true);
      setTimeout(() => { setOrderStatusUpdated(false); props.resetOrderStatusUpdate(); }, 3000);
    }

    if (location.pathname !== prevLocationPathnameRef.current) {
      prevLocationPathnameRef.current = location.pathname;
      setOrder({});
      props.getSelectedOrder(params.orderid);
    }

    if (props.adminOrders.order !== prevAdminOrders.order) {
      setOrder(props.adminOrders.order);
      setOrderStatus(props.adminOrders.order.orderStatus);
    }

    if (props.adminOrders.orderUpdated !== prevAdminOrders.orderUpdated && updateClicked) {
      setUpdateClicked(false);
      setShowUpdateMessage(true);
      setTimeout(() => setShowConfirmDelivery(false), 3000);
    }
  });

  const updateApproved = () => {
    props.updateDeliveryDate(params.orderid, {
      shippingDate: deliveryDate,
      orderStatus: 'Confirmed',
      ...(adminOrderMessage !== '' && { orderAdminMessage: adminOrderMessage }),
    });
    setUpdateClicked(true);
  };

  const updateOrderStatus = () => {
    if (order.orderStatus !== orderStatus) {
      props.orderStatusChange(params.orderid, { orderStatus });
    } else {
      setShowWarning(true);
    }
  };

  let SubTotal: any;
  if (order.products) {
    SubTotal = Math.round(
      order.products
        .map((p: any) => p.productQuantity * p.productPrice)
        .reduce((a: any, b: any) => a + b) * 100
    ) / 100;
  }

  const total = order.total / 100;
  let discount: any;
  if (order.coupon && SubTotal >= order.coupon.minimumSpend) {
    discount = order.coupon.isPercent ? (SubTotal / 100) * order.coupon.amount : order.coupon.amount;
  }

  const ct = order.user ? customerType(order.user.orders.length) : null;

  return (
    <div>
      {props.adminOrders.loading && (
        <div className="admin-empty-state">
          <i className="fas fa-spinner fa-spin" />
          <p>Loading order…</p>
        </div>
      )}

      {Object.keys(order).length !== 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}>

          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Order items */}
            <div className="admin-card">
              <div className="admin-card-header">
                <p className="admin-card-title">Order Details</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className={`status-badge ${order.orderStatus.toLowerCase()}`}>{order.orderStatus}</span>
                </div>
              </div>
              <div className="admin-card-body-flush">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Name</th>
                      <th>Qty</th>
                      <th>Price</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.products.map((product: any) => (
                      <tr key={product._id}>
                        <td style={{ width: 60 }}>
                          <img src={product.productImage} alt={product.productName} style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover' }} />
                        </td>
                        <td style={{ fontWeight: 600 }}>{product.productName}</td>
                        <td>{product.productQuantity}</td>
                        <td>AUD {product.productPrice.toFixed(2)}</td>
                        <td>AUD {(product.productPrice * product.productQuantity).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Other orders */}
            {order.user && order.user.orders.length > 1 && (
              <div className="admin-card">
                <div className="admin-card-header">
                  <p className="admin-card-title">Other orders by {ucFirst(order.firstName)}</p>
                </div>
                <div className="admin-card-body-flush">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>Total</th>
                        <th>Shipping</th>
                        <th>Payment</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {order.user.orders.filter((o: any) => o.orderid !== order.orderid).map((o: any) => (
                        <tr key={o._id} onClick={() => history.push({ pathname: `/adminpanel/orders/${o._id}`, state: { order: o } })}>
                          <td className="order-time">{moment(o.created_at).fromNow()}</td>
                          <td>AUD {(o.total / 100).toFixed(2)}</td>
                          <td>{ucFirst(o.shippingType)}</td>
                          <td>{ucFirst(o.paymentType)}</td>
                          <td><span className={`status-badge ${o.orderStatus.toLowerCase()}`}>{o.orderStatus}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Shipping summary */}
            <div className="admin-card">
              <div className="admin-card-header">
                <p className="admin-card-title">{order.shippingType === 'pickup' ? 'Pick Up' : 'Delivery'}</p>
                {ct && <span className="status-badge" style={{ background: ct.bg, color: ct.color }}>{ct.label}</span>}
              </div>
              <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <div style={infoRowStyle}><span style={sectionLabelStyle}>Ordered</span><span className="order-time">{moment(order.created_at).fromNow()}</span></div>
                <div style={infoRowStyle}><span style={sectionLabelStyle}>Name</span><span>{order.firstName} {order.lastName}</span></div>
                <div style={infoRowStyle}><span style={sectionLabelStyle}>Email</span><span>{order.email}</span></div>
                <div style={infoRowStyle}><span style={sectionLabelStyle}>Phone</span><span>{order.phoneNumber}</span></div>
                {order.shippingType === 'pickup' ? (
                  <>
                    <div style={infoRowStyle}><span style={sectionLabelStyle}>Pick up date</span><span>{moment(order.pickUpDate).format('L')}</span></div>
                    <div style={infoRowStyle}><span style={sectionLabelStyle}>Pick up time</span><span>{order.pickUpTime / 3600}:00</span></div>
                  </>
                ) : (
                  <>
                    <div style={infoRowStyle}><span style={sectionLabelStyle}>Address</span><span>{order.address}</span></div>
                    <div style={infoRowStyle}><span style={sectionLabelStyle}>Suburb</span><span>{order.suburb.toUpperCase()} VIC {order.postcode}</span></div>
                    {order.shippingDate && <div style={infoRowStyle}><span style={sectionLabelStyle}>Delivery date</span><span>{moment(order.shippingDate).format('Do MMM YYYY')}</span></div>}
                  </>
                )}

                {order.shippingType !== 'pickup' && order.orderStatus === 'Processing' && (
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontSize: '0.82rem', color: '#ec174f', marginBottom: 8 }}>Set a delivery date to confirm this order.</p>
                    <DatePicker
                      selected={deliveryDate}
                      onChange={setDeliveryDate}
                      placeholderText="Select delivery date"
                      minDate={moment().toDate()}
                    />
                    <textarea
                      style={{ ...{ width: '100%', padding: '7px 10px', border: '1px solid #e1e3e5', borderRadius: 6, fontSize: '0.82rem', marginTop: 8, resize: 'vertical', minHeight: 80 } }}
                      name="adminOrderMessage"
                      value={adminOrderMessage}
                      onChange={(e) => setAdminOrderMessage(e.target.value)}
                      placeholder="Notes for the buyer (attached to email)"
                    />
                    {!showConfirmDelivery ? (
                      <button
                        className="admin-dropdown-trigger"
                        style={{ marginTop: 8, background: '#ec174f', color: '#fff', borderColor: '#ec174f', opacity: deliveryDate === '' ? 0.4 : 1 }}
                        disabled={deliveryDate === ''}
                        onClick={() => setShowConfirmDelivery(true)}
                      >
                        Set delivery date
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                        <span style={{ fontSize: '0.78rem', color: '#6d7175' }}>
                          {showUpdateMessage ? 'Done! Buyer has been notified.' : `Confirm ${moment(deliveryDate).format('Do MMM YYYY')}?`}
                        </span>
                        {!showUpdateMessage && (
                          <>
                            <button className="admin-dropdown-trigger" style={{ background: '#ec174f', color: '#fff', borderColor: '#ec174f' }} onClick={updateApproved}>
                              {props.adminOrders.updateButtonLoading ? 'Saving…' : 'Confirm'}
                            </button>
                            <button className="admin-dropdown-trigger" onClick={() => setShowConfirmDelivery(false)}>Cancel</button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Order summary */}
            <div className="admin-card">
              <div className="admin-card-header">
                <p className="admin-card-title">Order Summary</p>
              </div>
              <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <div style={infoRowStyle}><span style={sectionLabelStyle}>Subtotal</span><span>AUD {SubTotal.toFixed(2)}</span></div>
                <div style={infoRowStyle}>
                  <span style={sectionLabelStyle}>Shipping</span>
                  <span>{order.shippingType !== 'pickup' ? `AUD ${order.shippingFee.toFixed(2)}` : 'Free'}</span>
                </div>
                {order.coupon && discount !== undefined && (
                  <>
                    <div style={infoRowStyle}>
                      <span style={sectionLabelStyle}>Coupon</span>
                      <span>{order.coupon.isPercent ? `${order.coupon.amount}%` : `AUD ${order.coupon.amount}`}</span>
                    </div>
                    <div style={infoRowStyle}>
                      <span style={{ ...sectionLabelStyle, color: '#ec174f' }}>Discount</span>
                      <span style={{ color: '#ec174f' }}>- AUD {discount.toFixed(2)}</span>
                    </div>
                  </>
                )}
                <div style={{ ...infoRowStyle, borderBottom: 'none', fontWeight: 700, fontSize: '0.95rem', marginTop: 4 }}>
                  <span>Total</span>
                  <span>AUD {total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Update status */}
            <div className="admin-card">
              <div className="admin-card-header">
                <p className="admin-card-title">Order Status</p>
              </div>
              <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <select
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e1e3e5', borderRadius: 6, fontSize: '0.85rem', color: '#202223', background: '#fff' }}
                  value={orderStatus}
                  name="orderStatus"
                  onChange={(e) => { setOrderStatus(e.target.value); setShowWarning(false); }}
                >
                  <option value="Processing">Processing</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>

                {showWarning && (
                  <span style={{ fontSize: '0.8rem', color: '#856404' }}>Status is already {order.orderStatus}.</span>
                )}
                {orderStatusUpdated && (
                  <span className="status-badge confirmed" style={{ fontSize: '0.78rem', alignSelf: 'flex-start' }}>
                    Updated to {order.orderStatus}
                  </span>
                )}

                <button
                  className="admin-dropdown-trigger"
                  style={{ alignSelf: 'flex-start', background: '#ec174f', color: '#fff', borderColor: '#ec174f' }}
                  onClick={updateOrderStatus}
                >
                  Update status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => ({
  adminOrders: state.adminOrders,
});

export default connect(mapStateToProps, {
  getSelectedOrder,
  updateDeliveryDate,
  resetSelectedOrder,
  orderStatusChange,
  resetOrderStatusUpdate,
})(SelectedOrder);
